import type { EntityBulkFetchRequest } from '@unifyapps/network/generated/models/entityBulkFetchRequest';
import type { EntityBulkFetchResponse } from '@unifyapps/network/generated/models/entityBulkFetchResponse';
import type {
  QueryClient,
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from '@unifyapps/network/react-query';
import { useQuery } from '@unifyapps/network/react-query';
import type { ErrorType } from '@unifyapps/network/utils/fetch';
import { executeFetch } from '@unifyapps/network/utils/fetch';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- same as in the generated hooks
type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

export const getAuthComponentBulkFetch = (
  entityBulkFetchRequest: EntityBulkFetchRequest,
  options?: SecondParameter<typeof executeFetch>,
  signal?: AbortSignal,
) => {
  return executeFetch<EntityBulkFetchResponse>(
    {
      url: `/auth/component/bulk-fetch`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: entityBulkFetchRequest,
      signal,
    },
    options,
  );
};

export const getAuthComponentBulkFetchQueryKey = (
  entityBulkFetchRequest: EntityBulkFetchRequest,
) => {
  return [`/auth/component/bulk-fetch`, entityBulkFetchRequest] as const;
};

export const getAuthComponentBulkFetchQueryOptions = <
  TData = Awaited<ReturnType<typeof getAuthComponentBulkFetch>>,
  TError = ErrorType<unknown>,
>(
  entityBulkFetchRequest: EntityBulkFetchRequest,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAuthComponentBulkFetch>>, TError, TData>
    >;
    request?: SecondParameter<typeof executeFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAuthComponentBulkFetchQueryKey(entityBulkFetchRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAuthComponentBulkFetch>>> = ({
    signal,
    meta,
  }) => getAuthComponentBulkFetch(entityBulkFetchRequest, { ...requestOptions, meta }, signal);

  return {
    queryKey,
    queryFn,
    staleTime: 5000,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getAuthComponentBulkFetch>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetAuthComponentBulkFetchQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAuthComponentBulkFetch>>
>;
export type GetAuthComponentBulkFetchQueryError = ErrorType<unknown>;

export const useGetAuthComponentBulkFetch = <
  TData = Awaited<ReturnType<typeof getAuthComponentBulkFetch>>,
  TError = ErrorType<unknown>,
>(
  entityBulkFetchRequest: EntityBulkFetchRequest,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAuthComponentBulkFetch>>, TError, TData>
    >;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getAuthComponentBulkFetchQueryOptions(entityBulkFetchRequest, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const prefetchGetAuthComponentBulkFetch = async <
  TData = Awaited<ReturnType<typeof getAuthComponentBulkFetch>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  entityBulkFetchRequest: EntityBulkFetchRequest,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAuthComponentBulkFetch>>, TError, TData>
    >;
  },
): Promise<QueryClient> => {
  const queryOptions = getAuthComponentBulkFetchQueryOptions(entityBulkFetchRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};
